import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Check, ArrowRight, Loader2, MessageCircle, Zap } from "lucide-react";
import { services } from "../data/services";
import { company } from "../data/company";
import { getUtm } from "../utils/utm";
import { trackConversion, trackOnce, trackClick } from "../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const steps = [
  { id: 1, label: "Dienst" },
  { id: 2, label: "Details" },
  { id: 3, label: "Contact" },
];

const budgets = ["< € 10.000", "€ 10.000 – 25.000", "€ 25.000 – 50.000", "€ 50.000 – 100.000", "> € 100.000", "Onbekend"];
const timelines = ["Zo snel mogelijk", "Binnen 1 maand", "1 – 3 maanden", "3 – 6 maanden", "Verkennen"];

// Lead scoring weights — mirror of backend logic (for instant UI feedback)
const BUDGET_W = { "< € 10.000": 1, "€ 10.000 – 25.000": 2, "€ 25.000 – 50.000": 3, "€ 50.000 – 100.000": 4, "> € 100.000": 5, "Onbekend": 1 };
const TIMELINE_W = { "Zo snel mogelijk": 5, "Binnen 1 maand": 4, "1 – 3 maanden": 3, "3 – 6 maanden": 2, "Verkennen": 1 };
const PREMIUM = new Set(["Complete Woning Renovatie", "Cascorenovatie", "Aanbouw", "Uitbouw", "Keuken Renovatie", "Badkamer Renovatie"]);

function scoreLead(f) {
  const b = BUDGET_W[f.budget] || 1;
  const t = TIMELINE_W[f.timeline] || 2;
  const bonus = PREMIUM.has(f.service) ? 2 : 0;
  const raw = b * t + bonus;
  const score = Math.round((raw / 27) * 100);
  const intent = score >= 65 ? "high" : score >= 35 ? "medium" : "low";
  return { score, intent };
}

export const QuoteForm = ({ initialService = "", compact = false }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const loc = useLocation();

  // Read ?bundel=isolatie-combi to prefill form for combi-package leads
  const bundle = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search).get("bundel");
    return p === "isolatie-combi" ? "isolatie-combi" : null;
  }, []);

  const [form, setForm] = useState({
    service: bundle === "isolatie-combi" ? "Isolatie Combi-pakket (Gevel + Dak + HR++ Glas)" : initialService,
    budget: "",
    timeline: "",
    postal_code: "",
    city: "",
    message: bundle === "isolatie-combi"
      ? "Ik ben geïnteresseerd in het Isolatie Combi-pakket: gevel isolatie + dakisolatie + HR++ of triple glas. Graag advies over de maximale ISDE-subsidie en een vaste totaalprijs voor deze 3 maatregelen samen."
      : "",
    name: "",
    email: "",
    phone: "",
  });

  const update = (k, v) => {
    // Fire once when the user first interacts with the form (real intent, not just page load)
    trackOnce("quote_started", { source_page: loc.pathname });
    setForm((f) => ({ ...f, [k]: v }));
  };

  const canNext1 = !!form.service;
  const canNext2 = true;
  const canSubmit = form.name.trim().length > 1 && /.+@.+\..+/.test(form.email) && form.phone.trim().length >= 6;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/quote`, { ...form, source_page: loc.pathname, utm: getUtm() });
      // Conversion tracking: value depends on intent (high = €100, medium = €50, low = €20)
      const { score, intent } = scoreLead(form);
      const value = intent === "high" ? 100 : intent === "medium" ? 50 : 20;
      trackConversion("quote", value, { service: form.service, intent, lead_score: score });
      setDone(true);
    } catch (err) {
      console.error(err);
      setError("Er ging iets mis. Probeer het opnieuw of bel ons direct.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const { score, intent } = scoreLead(form);
    const isHigh = intent === "high";
    const waMsg = encodeURIComponent(
      `Hallo Koning,\n\nIk heb zojuist een offerte aangevraagd via uw website.\n\n` +
      `• Dienst: ${form.service}\n` +
      (form.budget ? `• Budget: ${form.budget}\n` : "") +
      (form.timeline ? `• Planning: ${form.timeline}\n` : "") +
      (form.city ? `• Stad: ${form.city}\n` : "") +
      `\nNaam: ${form.name}\nTelefoon: ${form.phone}\n\nGraag hoor ik snel van u.`
    );
    const waLink = `${company.whatsappHref}?text=${waMsg}`;

    return (
      <div className="border border-koning-gold p-8 md:p-10" data-testid="quote-form-success">
        <div className="w-14 h-14 mx-auto rounded-none border border-koning-gold flex items-center justify-center mb-5">
          <Check className="text-koning-gold" size={24} />
        </div>
        <h3 className="font-display text-2xl md:text-3xl text-white text-center mb-2">Dank u — we nemen contact op</h3>
        <p className="text-koning-muted text-sm max-w-md mx-auto text-center">
          Uw aanvraag is ontvangen. We bellen u binnen één werkdag terug om de details te bespreken en een vrijblijvende offerte op te stellen.
        </p>

        {isHigh && (
          <div className="mt-8 bg-koning-bg border border-koning-gold p-6" data-testid="quote-form-high-intent">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 border border-koning-gold flex items-center justify-center text-koning-gold flex-shrink-0">
                <Zap size={18} />
              </div>
              <div className="flex-1">
                <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase mb-1">Prioriteit aanvraag</div>
                <h4 className="font-display text-white text-xl">Direct contact met de eigenaar</h4>
                <p className="text-koning-muted text-sm mt-2">
                  Voor een project zoals het uwe kunt u direct via WhatsApp contact opnemen met de eigenaar — uw bericht is al voor u klaargezet.
                </p>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("whatsapp", "quote-form-direct")}
                  className="btn-gold mt-5 inline-flex"
                  data-testid="quote-form-whatsapp-direct"
                >
                  <MessageCircle size={16} /> Direct via WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-koning-muted">
          Referentienummer: <span className="text-koning-text">prioriteit {score}/100</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`bg-koning-surface border border-koning-border ${compact ? "p-6 md:p-8" : "p-8 md:p-12"}`} data-testid="quote-form">
      {bundle === "isolatie-combi" && (
        <div className="mb-6 flex items-start gap-3 p-4 border border-koning-gold bg-koning-gold/5 rounded" data-testid="quote-form-bundle-badge">
          <div className="w-8 h-8 border border-koning-gold flex items-center justify-center text-koning-gold flex-shrink-0">
            <Zap size={14} />
          </div>
          <div>
            <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase mb-1">Combi-pakket geselecteerd</div>
            <p className="text-koning-text text-sm">Isolatie Combi (Gevel + Dak + HR++ Glas) — wij berekenen de maximale ISDE-subsidie voor u door.</p>
          </div>
        </div>
      )}

      {/* Steps indicator */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 ${step >= s.id ? "text-koning-gold" : "text-koning-muted"}`}>
              <div className={`w-7 h-7 border flex items-center justify-center text-xs ${step >= s.id ? "border-koning-gold" : "border-koning-border"}`}>
                {step > s.id ? <Check size={14} /> : s.id}
              </div>
              <span className="text-[11px] tracking-[0.2em] uppercase hidden sm:block">{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-px ${step > s.id ? "bg-koning-gold" : "bg-koning-border"}`} />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="quote-form-step-1">
          <label className="koning-label">Welke dienst zoekt u?</label>
          <select
            className="koning-input mt-1"
            value={form.service}
            onChange={(e) => update("service", e.target.value)}
            data-testid="quote-form-service"
          >
            <option value="">— Selecteer dienst —</option>
            <option value="Isolatie Combi-pakket (Gevel + Dak + HR++ Glas)">★ Isolatie Combi-pakket (Gevel + Dak + HR++ Glas)</option>
            {services.map((s) => (
              <option key={s.slug} value={s.title}>{s.title}</option>
            ))}
            <option value="Anders / weet ik nog niet">Anders / weet ik nog niet</option>
          </select>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div>
              <label className="koning-label">Geschat budget</label>
              <select className="koning-input mt-1" value={form.budget} onChange={(e) => update("budget", e.target.value)} data-testid="quote-form-budget">
                <option value="">— Optioneel —</option>
                {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="koning-label">Gewenste planning</label>
              <select className="koning-input mt-1" value={form.timeline} onChange={(e) => update("timeline", e.target.value)} data-testid="quote-form-timeline">
                <option value="">— Optioneel —</option>
                {timelines.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-10 flex justify-end">
            <button
              type="button"
              className="btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={!canNext1}
              onClick={() => setStep(2)}
              data-testid="quote-form-next-1"
            >
              Volgende stap <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="quote-form-step-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="koning-label">Postcode</label>
              <input className="koning-input" placeholder="3071 AA" value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} data-testid="quote-form-postal" />
            </div>
            <div>
              <label className="koning-label">Stad</label>
              <input className="koning-input" placeholder="Rotterdam" value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="quote-form-city" />
            </div>
          </div>
          <div className="mt-6">
            <label className="koning-label">Vertel kort over uw project (optioneel)</label>
            <textarea
              rows="4"
              className="koning-input resize-none"
              placeholder="Bijvoorbeeld: badkamer van 8m², willen graag inloopdouche, vloerverwarming en marmerlook tegels..."
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              data-testid="quote-form-message"
            />
          </div>
          <div className="mt-10 flex justify-between">
            <button type="button" className="btn-outline" onClick={() => setStep(1)} data-testid="quote-form-back-2">Terug</button>
            <button type="button" className="btn-gold" onClick={() => setStep(3)} data-testid="quote-form-next-2">Volgende stap <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="quote-form-step-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="koning-label">Uw naam *</label>
              <input className="koning-input" placeholder="Voor- en achternaam" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="quote-form-name" />
            </div>
            <div>
              <label className="koning-label">Telefoonnummer *</label>
              <input className="koning-input" placeholder="06 12 34 56 78" value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="quote-form-phone" />
            </div>
            <div className="md:col-span-2">
              <label className="koning-label">E-mailadres *</label>
              <input type="email" className="koning-input" placeholder="naam@voorbeeld.nl" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="quote-form-email" />
            </div>
          </div>
          {error && <div className="mt-4 text-sm text-red-400">{error}</div>}
          <p className="mt-6 text-xs text-koning-muted leading-relaxed">
            Door te verzenden gaat u akkoord met onze privacy­voorwaarden. Wij gebruiken uw gegevens uitsluitend om uw aanvraag te beantwoorden.
          </p>
          <div className="mt-8 flex justify-between">
            <button type="button" className="btn-outline" onClick={() => setStep(2)} data-testid="quote-form-back-3">Terug</button>
            <button type="submit" className="btn-gold disabled:opacity-50" disabled={!canSubmit || submitting} data-testid="quote-form-submit">
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Verzenden…</> : <>Offerte aanvragen <ArrowRight size={16} /></>}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};
